import {
  ghostBtnClass,
  inputClass,
  mfgConfirmDialogClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  emptyComposeSlot,
  type ComposeSlotDef,
} from "@/lib/manufacturing/composePlan";
import { describeThresholdSpec } from "@/lib/manufacturing/composePlan/predicates";

function patchSlot(
  slots: ComposeSlotDef[],
  index: number,
  patch: Partial<ComposeSlotDef>,
): ComposeSlotDef[] {
  return slots.map((s, i) => (i === index ? { ...s, ...patch } : s));
}

function Flag({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-600 dark:text-slate-300">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

export function ManufacturingModelsComposeSlotsEditor({
  slots,
  onChange,
}: {
  slots: ComposeSlotDef[];
  onChange: (slots: ComposeSlotDef[]) => void;
}) {
  return (
    <div className={`space-y-2 ${mfgConfirmDialogClass.replace("space-y-3 p-4", "p-3")}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Slot labels
        </div>
        <button
          type="button"
          className={ghostBtnClass}
          onClick={() =>
            onChange([
              ...slots,
              emptyComposeSlot({
                key: `slot_${slots.length + 1}`,
                label: "New slot",
                description: "new slot",
              }),
            ])
          }
        >
          Add slot
        </button>
      </div>
      <p className="text-[11px] text-slate-500">
        Series-common assemblies. Description = AI-ready standard fill; when “changes with size”
        is on, threshold + secondary description pick the above-threshold fill.
      </p>
      <div className="space-y-3">
        {slots.map((slot, index) => (
          <div
            key={`${slot.key}-${index}`}
            className="space-y-2 rounded-md border border-slate-200 bg-white p-2 dark:border-zinc-700 dark:bg-zinc-950"
          >
            <div className="flex flex-wrap items-start gap-2">
              <label className="block min-w-[7rem] flex-1 text-xs">
                Key
                <input
                  className={`${inputClass} mt-1 font-mono text-xs`}
                  value={slot.key}
                  onChange={(e) => {
                    const key = e.target.value.trim().toLowerCase().replace(/\s+/g, "_");
                    onChange(
                      patchSlot(slots, index, {
                        key,
                        label: slot.label === slot.key ? key : slot.label,
                      }),
                    );
                  }}
                  placeholder="door"
                />
              </label>
              <label className="block min-w-[7rem] flex-1 text-xs">
                Label
                <input
                  className={`${inputClass} mt-1`}
                  value={slot.label}
                  onChange={(e) => onChange(patchSlot(slots, index, { label: e.target.value }))}
                  placeholder="Door"
                />
              </label>
              <button
                type="button"
                className={`${ghostBtnClass} mt-5`}
                onClick={() => onChange(slots.filter((_, i) => i !== index))}
              >
                Remove
              </button>
            </div>
            <label className="block text-xs">
              Standard fill (AI-ready)
              <input
                className={`${inputClass} mt-1`}
                value={slot.description}
                onChange={(e) =>
                  onChange(patchSlot(slots, index, { description: e.target.value }))
                }
                placeholder="single wooden shed door"
              />
            </label>
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              <Flag
                label="series specific"
                checked={slot.seriesSpecific}
                onChange={(v) => onChange(patchSlot(slots, index, { seriesSpecific: v }))}
              />
              <Flag
                label="size specific"
                checked={slot.sizeSpecific}
                onChange={(v) => onChange(patchSlot(slots, index, { sizeSpecific: v }))}
              />
              <Flag
                label="style specific"
                checked={slot.styleSpecific}
                onChange={(v) => onChange(patchSlot(slots, index, { styleSpecific: v }))}
              />
              <Flag
                label="changes with size"
                checked={slot.changesWithSize}
                onChange={(v) => onChange(patchSlot(slots, index, { changesWithSize: v }))}
              />
            </div>
            {slot.changesWithSize ? (
              <div className="grid gap-2 sm:grid-cols-2">
                <label className="block text-xs">
                  Threshold size
                  <input
                    className={`${inputClass} mt-1`}
                    value={slot.thresholdSize}
                    onChange={(e) =>
                      onChange(patchSlot(slots, index, { thresholdSize: e.target.value }))
                    }
                    placeholder="10x12 or Wx12"
                  />
                  <span className="mt-1 block text-[11px] text-slate-500">
                    {describeThresholdSpec(slot.thresholdSize)}
                  </span>
                </label>
                <label className="block text-xs">
                  Above-threshold fill (AI-ready)
                  <input
                    className={`${inputClass} mt-1`}
                    value={slot.aboveDescription}
                    onChange={(e) =>
                      onChange(patchSlot(slots, index, { aboveDescription: e.target.value }))
                    }
                    placeholder="double wooden shed door"
                  />
                </label>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
